import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Compass, Leaf, Heart, Smile, Landmark, Palette, Flower2, Mountain, Sparkles, ShieldCheck, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ListingCard from "@/components/ListingCard";

const CATEGORY_META = {
  adventure:  { Icon: Mountain,  grad: "iv-cat-adventure",  ring: "iv-cat-ring-adventure",  badge: "bg-emerald-100 text-emerald-900"   },
  explore:    { Icon: Compass,   grad: "iv-cat-explore",    ring: "iv-cat-ring-explore",    badge: "bg-sky-100 text-sky-900"           },
  medical:    { Icon: Heart,     grad: "iv-cat-medical",    ring: "iv-cat-ring-medical",    badge: "bg-teal-100 text-teal-900"         },
  relaxation: { Icon: Leaf,      grad: "iv-cat-relaxation", ring: "iv-cat-ring-relaxation", badge: "bg-lime-100 text-lime-900"         },
  fun:        { Icon: Smile,     grad: "iv-cat-fun",        ring: "iv-cat-ring-fun",        badge: "bg-amber-100 text-amber-900"       },
  historic:   { Icon: Landmark,  grad: "iv-cat-historic",   ring: "iv-cat-ring-historic",   badge: "bg-stone-100 text-stone-900"       },
  cultural:   { Icon: Palette,   grad: "iv-cat-cultural",   ring: "iv-cat-ring-cultural",   badge: "bg-pink-100 text-pink-900"         },
  spiritual:  { Icon: Flower2,   grad: "iv-cat-spiritual",  ring: "iv-cat-ring-spiritual",  badge: "bg-orange-100 text-orange-900"     },
};

const HERO_COLLAGE = [
  "https://images.unsplash.com/photo-1564507592333-c60657eea523?w=1200",
  "https://images.unsplash.com/photo-1524229321985-1e1989075d9b?w=1200",
  "https://images.unsplash.com/photo-1545389336-cf090694435e?w=1200",
  "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=1200",
];

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        await api.post("/seed/demo").catch(() => {});
        const [c, l] = await Promise.all([
          api.get("/categories"),
          api.get("/listings/featured?limit=8"),
        ]);
        setCategories(c.data);
        setFeatured(l.data);
      } catch (e) { /* ignore */ }
    };
    load();
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const s = q.trim();
    if (!s) return;
    window.location.href = `/search?q=${encodeURIComponent(s)}`;
  };

  return (
    <div data-testid="home-page" className="iv-pattern-mandala">
      {/* Hero */}
      <section className="relative overflow-hidden iv-hero-grad" data-testid="home-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-14 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1 rounded-full bg-[hsl(var(--saffron))] text-white shadow-[0_6px_14px_-8px_rgba(242,149,41,0.7)]" data-testid="hero-tag">
              <Sparkles className="h-3.5 w-3.5" /> Curated for international travelers
            </span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              Discover <span className="text-[hsl(var(--vermilion))]">India</span>,<br />
              <span className="bg-gradient-to-r from-[hsl(var(--saffron))] via-[hsl(var(--vermilion))] to-[hsl(var(--rose-indian))] bg-clip-text text-transparent">curated.</span> Pick a mood, not a map.
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground/70 max-w-xl">From Himalayan treks and Kerala backwaters to Ayurveda retreats and heritage palaces — shop experiences by interest, with transparent pricing.</p>

            <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row items-stretch gap-2 max-w-xl" data-testid="home-hero-search">
              <div className="flex items-center flex-1 rounded-2xl bg-card border border-border px-3 shadow-[0_10px_30px_-22px_rgba(31,42,68,0.35)]">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Try 'Taj Mahal', 'yoga', 'Kerala'..." className="border-0 focus-visible:ring-0 bg-transparent text-base" data-testid="home-hero-search-input" />
              </div>
              <Button type="submit" className="rounded-2xl h-12 px-5 bg-gradient-to-r from-[hsl(var(--vermilion))] to-[hsl(var(--saffron))] text-white hover:brightness-110" data-testid="home-hero-search-submit">Search trips</Button>
            </form>

            <div className="mt-5 flex flex-wrap gap-2">
              {["adventure", "spiritual", "historic", "relaxation"].map((c) => {
                const M = CATEGORY_META[c];
                return (
                  <Link key={c} to={`/categories/${c}`} className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-card border border-border hover:bg-muted transition-colors`} data-testid={`hero-chip-${c}`}>
                    <M.Icon className="h-3.5 w-3.5" /> {c[0].toUpperCase() + c.slice(1)}
                  </Link>
                );
              })}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-foreground/70">
              <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--emerald-india))]" /> Vetted hosts</span>
              <span className="inline-flex items-center gap-1"><Globe className="h-3.5 w-3.5 text-[hsl(var(--peacock))]" /> Global traveler support</span>
              <span className="inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-[hsl(var(--saffron))]" /> Transparent, all-inclusive pricing</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative hidden lg:block">
            <div className="grid grid-cols-2 gap-4">
              {HERO_COLLAGE.map((src, i) => (
                <div key={`hero-image-${i}-${src.split('?')[0].split('/').pop()}`} className={`rounded-3xl overflow-hidden aspect-[4/5] ring-1 ring-white/60 shadow-[0_20px_60px_-30px_rgba(31,42,68,0.55)] ${i === 1 ? "mt-10" : ""} ${i === 2 ? "-mt-6" : ""}`}>
                  <img src={src} alt="India" className="h-full w-full object-cover" />
                </div>
              ))}
            </div>
            {/* Decorative festival dots */}
            <div className="absolute -bottom-3 left-4 right-4 h-1 rounded-full iv-tricolor opacity-80" />
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-14 md:py-18">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Browse by interest</h2>
              <p className="text-muted-foreground mt-1">Eight ways to fall in love with India.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="home-category-grid">
            {categories.map((c) => {
              const M = CATEGORY_META[c.id] || CATEGORY_META.explore;
              return (
                <Link key={c.id} to={`/categories/${c.id}`} className={`group relative rounded-2xl p-5 overflow-hidden border border-border ${M.grad} ${M.ring} hover:-translate-y-0.5 hover:shadow-[0_20px_45px_-22px_rgba(31,42,68,0.35)] transition-shadow`} data-testid={`home-category-tile-${c.id}`}>
                  {/* subtle paisley watermark */}
                  <div className="absolute -right-6 -bottom-6 opacity-20" aria-hidden>
                    <M.Icon className="h-28 w-28" />
                  </div>
                  <div className="relative">
                    <div className="h-10 w-10 rounded-xl bg-card/80 backdrop-blur-sm text-foreground flex items-center justify-center border border-white/50">
                      <M.Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 font-display text-lg font-semibold">{c.name}</h3>
                    <p className="text-sm text-foreground/70">{c.tagline}</p>
                    <div className="mt-3 inline-flex items-center gap-2 text-xs font-medium">
                      <span className={`px-2 py-0.5 rounded-full ${M.badge}`}>{c.count} experiences</span>
                      <span className="text-foreground/60 group-hover:text-foreground">Explore →</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured listings */}
      <section className="py-12 md:py-16 iv-pattern-rangoli" data-testid="featured-listings-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Handpicked experiences</h2>
              <p className="text-muted-foreground mt-1">Top-rated journeys from vetted local hosts.</p>
            </div>
            <Link to="/search" className="text-sm font-medium text-[hsl(var(--primary))]">See all →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((l) => (
              <ListingCard key={l.listing_id} listing={l} />
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-14" data-testid="how-it-works-section">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
          <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-center">How IndoVoyage works</h2>
          <div className="mt-8 grid sm:grid-cols-3 gap-5">
            {[
              { n: "01", t: "Pick a mood", d: "Choose among 8 curated interest categories.", color: "from-[hsl(var(--saffron))] to-[hsl(var(--vermilion))]" },
              { n: "02", t: "Compare transparently", d: "All-inclusive pricing. No hidden charges. No surprises.", color: "from-[hsl(var(--peacock))] to-[hsl(var(--emerald-india))]" },
              { n: "03", t: "Book & travel", d: "Confirm dates instantly and get ready to explore.", color: "from-[hsl(var(--rose-indian))] to-[hsl(var(--saffron))]" },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl bg-card border border-border p-6 relative overflow-hidden">
                <div className={`absolute -top-8 -right-8 h-24 w-24 rounded-full bg-gradient-to-br ${s.color} opacity-25`} />
                <div className="relative">
                  <div className="text-sm font-semibold text-[hsl(var(--vermilion))]">{s.n}</div>
                  <h3 className="mt-2 font-display text-lg font-semibold">{s.t}</h3>
                  <p className="text-muted-foreground text-sm mt-1">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vendor CTA */}
      <section className="py-14" data-testid="vendor-cta-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="rounded-3xl overflow-hidden border border-border grid md:grid-cols-2 iv-pattern-paisley">
            <div className="p-8 md:p-12">
              <span className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1 rounded-full bg-[hsl(var(--emerald-india))] text-white">For local hosts</span>
              <h3 className="mt-3 font-display text-2xl md:text-3xl font-semibold">Are you a local host?</h3>
              <p className="text-muted-foreground mt-2 max-w-md">Put your experiences in front of curious international travelers. Only pay a flat 10% listing fee included in the customer price.</p>
              <Link to="/become-a-vendor"><Button className="mt-5 rounded-xl bg-gradient-to-r from-[hsl(var(--vermilion))] to-[hsl(var(--saffron))] text-white hover:brightness-110" data-testid="home-cta-become-vendor">Become a vendor</Button></Link>
            </div>
            <div className="relative min-h-60">
              <img src="https://images.unsplash.com/photo-1599661046289-e31897846e41?w=1600" alt="Vendor" className="absolute inset-0 h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
